/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { remoteSessionClientsPrepareEMA } from "../funcs/remoteSessionClientsPrepareEMA.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { IdentityChainingPreparation } from "../models/components/identitychainingpreparation.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  PrepareEMARequest,
  PrepareEMASecurity,
} from "../models/operations/prepareema.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RemoteSessionClientsPrepareEMAMutationVariables = {
  request: PrepareEMARequest;
  security?: PrepareEMASecurity | undefined;
  options?: RequestOptions;
};

export type RemoteSessionClientsPrepareEMAMutationData =
  IdentityChainingPreparation;

export type RemoteSessionClientsPrepareEMAMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * prepareEMA remoteSessionClients
 *
 * @remarks
 * Explicit, tenant-scoped identity-chaining configuration. Mutations require project-write authorization; reads require project-read authorization. Does not exchange tokens or establish provider verification, trust, consent, or user access.
 */
export function useRemoteSessionClientsPrepareEMAMutation(
  options?: MutationHookOptions<
    RemoteSessionClientsPrepareEMAMutationData,
    RemoteSessionClientsPrepareEMAMutationError,
    RemoteSessionClientsPrepareEMAMutationVariables
  >,
): UseMutationResult<
  RemoteSessionClientsPrepareEMAMutationData,
  RemoteSessionClientsPrepareEMAMutationError,
  RemoteSessionClientsPrepareEMAMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRemoteSessionClientsPrepareEMAMutation(client, options),
    ...options,
  });
}

export function mutationKeyRemoteSessionClientsPrepareEMA(): MutationKey {
  return ["@gram/client", "remoteSessionClients", "prepareEMA"];
}

export function buildRemoteSessionClientsPrepareEMAMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RemoteSessionClientsPrepareEMAMutationVariables,
  ) => Promise<RemoteSessionClientsPrepareEMAMutationData>;
} {
  return {
    mutationKey: mutationKeyRemoteSessionClientsPrepareEMA(),
    mutationFn: function remoteSessionClientsPrepareEMAMutationFn({
      request,
      security,
      options,
    }): Promise<RemoteSessionClientsPrepareEMAMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(remoteSessionClientsPrepareEMA(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
